<script>
import Form from '@/components/ui/widgets/Form.vue'
import FormSection from '@/components/ui/widgets/FormSection.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import FeatureToggle from '@/components/cars/edit/FeatureToggle.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import Notice from '@/components/cars/edit/Notice.vue'
import {
  deleteLeadPotentialHistoryItem, getLeadPotentialHistory, getLeadPotentialSettings, updateLeadPotentialSettings
} from '@/api/leads'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import { mapGetters } from 'vuex'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import { isEmpty } from '@/helpers/utils'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'

export default {
  components: {
    'mia-feature-input': FeatureInput,
    'mia-feature-select': FeatureSelect,
    'mia-feature-toggle': FeatureToggle,
    'mia-form': Form,
    'mia-form-section': FormSection,
    'mia-form-section-grid': FormSectionGrid,
    'mia-notice': Notice,
    'mia-submit-button': SubmitButton
  },

  mixins: [FormattingHelpers, HandlesBackendValidationErrors],

  data() {
    return {
      form: {
        item_active: 'N',
        item_escalation_days: '',
        item_process_lead_id: '',
        item_max_leads_per_day: '',
        item_settings: {
          item_months_before_end: ''
        }
      },
      history: [],
      historyColumns: [
        {
          field: 'item_metadata.item_lead_id',
          label: 'Lead',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/lead/dashboard/:item_lead_id',
            labelProp: row => row.item_metadata.item_lead_id,
            schemaIds: row => ({
              item_lead_id: row.item_metadata.item_lead_id
            }),
            external: true
          }
        },
        {
          field: 'item_created_at',
          label: 'Erstellt am',
          headerAlign: 'left',
          representedAs: row => this.formatAsDateTime(row.item_created_at, 'sql')
        },
        {
          field: 'item_car_mobile_make',
          label: 'Marke',
          headerAlign: 'left'
        },
        {
          field: 'item_car_mobile_model',
          label: 'Modell',
          headerAlign: 'left'
        },
        {
          field: 'item_car_id',
          label: 'Fahrzeug',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:item_car_id',
            labelProp: row => `[${row.item_car_id}] - ${row.item_car_fin || 'Ohne FIN'}`,
            external: true
          }
        },
        {
          field: 'item_metadata.item_lead_assignee_id',
          label: 'Zugewiesen an',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/user/profile/:item_lead_assignee_id',
            labelProp: row => `${row.item_metadata.resolved.item_lead_assignee.item_firstname} ${row.item_metadata.resolved.item_lead_assignee.item_lastname}`.trim(),
            schemaIds: row => ({
              item_lead_assignee_id: row.item_metadata.item_lead_assignee_id
            }),
            external: true
          }
        },
        {
          field: 'item_metadata.item_seller_id',
          label: 'Verkäufer',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/user/profile/:item_seller_id',
            labelProp: row => `${row.item_metadata.resolved.item_seller.item_firstname} ${row.item_metadata.resolved.item_seller.item_lastname}`.trim(),
            schemaIds: row => ({
              item_seller_id: row.item_metadata.item_seller_id
            }),
            external: true
          }
        },
        {
          field: 'item_metadata.item_customer_id',
          label: 'Kunde',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/customers/:item_customer_id',
            labelProp: row => this.getCustomerName(row.item_metadata.resolved.item_customer),
            schemaIds: row => ({
              item_customer_id: row.item_metadata.item_customer_id
            }),
            external: true
          }
        },
        {
          field: 'item_metadata.item_financing_term_end',
          label: 'Finanzierungs-/Leasingende',
          headerAlign: 'left',
          representedAs: row => this.formatAsDate(row.item_metadata.item_financing_term_end)
        },
        {
          field: 'item_metadata.item_calculated_month_until_financing_term_ends',
          label: 'Err. Finanzierungs-/Leasingende',
          headerAlign: 'left',
          representedAs: row => `${row.item_metadata.item_calculated_month_until_financing_term_ends} Monat${row.item_metadata.item_calculated_month_until_financing_term_ends === 1 ? '' : 'e'}`
        },
        {
          field: '',
          label: 'Aktion',
          headerAlign: 'left',
          component: ActionButtonColumn,
          meta: {
            actions: [
              {
                label: 'Löschen',
                attrs: {
                  title: 'Löschen'
                },
                class: 'border-red-500 text-red-500 hover:bg-red-500',
                onClick: (item) => {
                  this.historyItemToDelete = item
                }
              }
            ]
          }
        }
      ],
      state: {
        isCollapsed: false
      },
      historyItemToDelete: null
    }
  },

  computed: {
    ...mapGetters('lead', ['leadTypes']),

    persistedStateKey() {
      return 'carana_lead_potential_settings_eft_state'
    },

    persistedState() {
      const state = window.localStorage.getItem(this.persistedStateKey)
      const defaultSettings = {
        isCollapsed: false
      }

      try {
        return state ? JSON.parse(state) : defaultSettings
      } catch (e) {
        return defaultSettings
      }
    },

    processLeadTypes() {
      return this.leadTypes.filter(leadType => parseInt(leadType.item_is_process, 10) === 1)
    }
  },

  watch: {
    state: {
      deep: true,
      handler(newState) {
        this.persistState(newState)
      }
    }
  },

  created() {
    this.initPersistedState()
    this.loadSettings()
    this.loadHistory()
    this.$store.dispatch('lead/loadLeadTypes')
  },

  methods: {
    initPersistedState() {
      this.$set(this.state, 'isCollapsed', this.persistedState.isCollapsed)
    },

    persistState(state) {
      window.localStorage.setItem(this.persistedStateKey, JSON.stringify({ ...state }))
    },

    async loadSettings() {
      this.$wait.start('loading lead potential settings')
      const [error, response] = await getLeadPotentialSettings('ending_financing_terms')
      this.$wait.end('loading lead potential settings')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Einstellungen konnten nicht geladen werden']
        })
        return
      }

      this.initFormFromResponse(response)
    },

    initFormFromResponse(response) {
      this.$set(this.form, 'item_active', response?.item_active || 'N')
      this.$set(this.form, 'item_escalation_days', response?.item_lead_settings?.item_escalation_days || '')
      this.$set(this.form, 'item_process_lead_id', response?.item_lead_settings?.item_process_lead_id || '')
      this.$set(this.form, 'item_max_leads_per_day', response?.item_lead_settings?.item_max_leads_per_day || '')
      this.$set(this.form.item_settings, 'item_months_before_end', response?.item_settings?.item_months_before_end || '')
    },

    async loadHistory() {
      this.$wait.start('loading lead potential history')
      const [error, response] = await getLeadPotentialHistory('ending_financing_terms')
      this.$wait.end('loading lead potential history')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Historie konnte nicht geladen werden']
        })
        return
      }

      this.history = response
    },

    async save() {
      if (this.$wait.is('updating leading potential settings')) {
        return
      }

      this.$wait.start('updating leading potential settings')
      const [error, response] = await updateLeadPotentialSettings('ending_financing_terms', {
        item_active: this.form.item_active,
        item_settings: this.form.item_settings,
        item_lead_settings: {
          item_escalation_days: this.form.item_escalation_days || null,
          item_process_lead_id: this.form.item_process_lead_id || null,
          item_max_leads_per_day: this.form.item_max_leads_per_day || null
        }
      })
      this.$wait.end('updating leading potential settings')

      if (error) {
        this.handleBackendValidationErrors(error)
        window.eventBus.$emit('mia-error', {
          error: ['Daten konnten nicht gespeichert werden']
        })
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Daten erfolgreich gespeichert']
      })

      this.initFormFromResponse(response)
    },

    onEnabledInput(isChecked) {
      this.$set(this.form, 'item_active', isChecked ? 'Y' : 'N')
    },

    getCustomerName(customer) {
      const company = customer.item_company ? `${customer.item_company}, ` : ''
      const prefix = [customer.item_salutation, customer.item_title].filter(value => !isEmpty(value))
      const suffix = [customer.item_name, customer.item_firstname].filter(value => !isEmpty(value))

      return `${company}${prefix.join(' ')} ${suffix.join(', ')}`.trim()
    },

    async onDeleteLeadPotentialHistoryItemConfirmed() {
      if (this.$wait.is('deleting lead potential history item')) {
        return
      }

      this.$wait.start('deleting lead potential history item')
      const [error] = await deleteLeadPotentialHistoryItem('ending_financing_terms', this.historyItemToDelete.id)
      this.$wait.end('deleting lead potential history item')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Historien-Eintrag konnte nicht gelöscht werden']
        })
        return
      }

      this.history = this.history.filter(item => item.id !== this.historyItemToDelete.id)
      window.eventBus.$emit('mia-info', {
        info: ['Historien-Eintrag erfolgreich gelöscht']
      })
      this.historyItemToDelete = null
    }
  }
}
</script>

<template>
  <mia-form @submit="save">
    <template #default>
      <mia-form-section
        title="FD Ausläufer"
        description="Potential für Fahrzeuge deren Finanzierung / Leasing demnächst enden"
      >
        <mia-wait for="loading lead potential settings">
          <template #waiting>
            <div class="flex items-center justify-center py-8">
              <mia-wave-loader />
            </div>
          </template>

          <template #default>
            <mia-form-section-grid>
              <div class="col-span-2">
                <mia-notice permanent>
                  <p>Das Potential für FD Ausläufer überwacht <strong>Fahrzeugverkäufe</strong> mit der <strong>Zahlungsart Finanzierung sowie Leasing</strong>. Zum Ende der Finanzierungs- bzw. Leasinglaufzeit unter Berücksichtigung des eingestellten Monatspuffers generiert das System automatisch neue Verkaufschancen.</p>
                  <p class="mb-0">
                    Die Verkaufschancen werden automatisch <strong>an den letzten Verkäufer</strong> des Fahrzeugs verteilt. Sollte dies nicht möglich sein, wird die Verkaufschance in den Leadpool übertragen und dann automatisch verteilt.
                  </p>
                </mia-notice>
              </div>

              <template v-if="!state.isCollapsed">
                <div class="col-span-2 text-lg">
                  Allgemein
                </div>
                <div class="col-span-2 grid grid-cols-3 gap-6">
                  <div>
                    <mia-feature-toggle
                      label="Aktiv"
                      label-position="top"
                      :value="form.item_active === 'Y'"
                      @input="onEnabledInput"
                    />
                  </div>
                </div>
                <div class="col-span-2 flex items-center">
                  <div class="border-t border-gray-300 w-full" />
                </div>
                <div class="col-span-2 text-lg">
                  Potential
                </div>
                <div>
                  <mia-feature-input
                    v-model="form.item_settings.item_months_before_end"
                    type="number"
                    min="1"
                    label="Monate vor Ende der Leasing-/Finanzierung"
                    addon="Monate"
                    :errors="getErrors('item_settings.item_months_before_end')"
                    @input="clearErrors('item_settings.item_months_before_end')"
                  />
                </div>
                <div class="col-span-2 flex items-center">
                  <div class="border-t border-gray-300 w-full" />
                </div>
                <div class="col-span-2 text-lg">
                  Verkaufschance
                </div>
                <div>
                  <mia-feature-select
                    v-model="form.item_process_lead_id"
                    label="Leadtyp"
                    :errors="getErrors('item_lead_settings.item_process_lead_id')"
                    @input="clearErrors('item_lead_settings.item_process_lead_id')"
                  >
                    <template #options>
                      <option value="">
                        Bitte wählen
                      </option>
                      <option
                        v-for="leadType in processLeadTypes"
                        :key="leadType.id"
                        :value="leadType.id"
                      >
                        {{ leadType.item_title }}
                      </option>
                    </template>
                  </mia-feature-select>
                </div>
                <div>
                  <mia-feature-input
                    v-model="form.item_escalation_days"
                    type="number"
                    min="0"
                    label="Eskalationszeit"
                    addon="Tage"
                    :errors="getErrors('item_lead_settings.item_escalation_days')"
                    @input="clearErrors('item_lead_settings.item_escalation_days')"
                  />
                </div>
                <div>
                  <mia-feature-input
                    v-model="form.item_max_leads_per_day"
                    type="number"
                    min="0"
                    label="Max. Leads pro Tag"
                    :errors="getErrors('item_lead_settings.item_max_leads_per_day')"
                    @input="clearErrors('item_lead_settings.item_max_leads_per_day')"
                  />
                </div>
              </template>
            </mia-form-section-grid>
          </template>
        </mia-wait>
      </mia-form-section>

      <mia-wait
        v-if="!state.isCollapsed"
        for="loading lead potential history"
      >
        <template #waiting>
          <div class="flex items-center justify-center py-8">
            <mia-wave-loader />
          </div>
        </template>

        <template #default>
          <div class="bg-gray-300 p-4">
            Historie
          </div>
          <datatable
            class="w-full table-auto datatable"
            :data="history"
            :columns="historyColumns"
          >
            <template #no-results>
              <div class="text-center">
                Für dieses Potential wurden noch keine Leads angelegt.
              </div>
            </template>
          </datatable>

          <mia-confirmation-modal
            v-if="historyItemToDelete"
            id="delete-lead-potential-history-item-confirmation-modal"
            :show="historyItemToDelete !== null"
            title="Lead löschen"
            confirm-classes="border-red-500 bg-red-500 text-white hover:bg-red-600 hover:border-red-600"
            confirm-text="Lead löschen"
            cancel-text="Abbrechen"
            width="30%"
            :is-loading="$wait.is('deleting lead potential history item')"
            @closed="historyItemToDelete = null"
            @cancel="historyItemToDelete = null"
            @confirm="onDeleteLeadPotentialHistoryItemConfirmed"
          >
            <div class="p-4">
              Sind Sie sicher, dass Sie diesen Lead endgültig löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </div>
          </mia-confirmation-modal>
        </template>
      </mia-wait>
    </template>

    <template #actions>
      <div class="flex-1 flex items-center justify-between">
        <div>
          <button
            type="button"
            class="text-orange-mia hover:underline inline-flex items-center focus:outline-none"
            @click="$set(state, 'isCollapsed', !state.isCollapsed)"
          >
            <mia-streamline-icon
              src="arrow-up-1"
              class="w-4 h-4 mr-2 fill-current transition-transform ease-out duration-200"
              :class="state.isCollapsed ? 'rotate-180' : ''"
            />
            <span>{{ state.isCollapsed ? 'mehr' : 'weniger' }} anzeigen</span>
          </button>
        </div>

        <mia-submit-button
          size="small"
          label="Speichern"
          :is-loading="$wait.is('updating leading potential settings')"
        />
      </div>
    </template>
  </mia-form>
</template>
